/**
 * Firebase Auth client + WebSocket JWT binding.
 *
 * Auth only: sign-in methods are re-exported for `AuthContext` and the auth
 * screens; the Firestore data path lives in `./rethink-firestore` (the
 * Firestore-mirror stubs that used to sit here were dead code — nothing
 * imported them).
 */
import { initializeApp, getApps, getApp } from 'firebase/app';
import {
  getAuth,
  GoogleAuthProvider,
  signInWithPopup,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendEmailVerification,
  applyActionCode,
  signOut,
  onAuthStateChanged,
  updateProfile,
  type User,
} from 'firebase/auth';

import { rethink } from './client';

export type { User };

const env = import.meta.env as Record<string, string | undefined>;

// 1. Initialize Safe-Fail Firebase Client configuration
const firebaseConfig = {
  apiKey: env.VITE_FIREBASE_API_KEY,
  authDomain: env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: env.VITE_FIREBASE_APP_ID,
};

export const firebaseConfigured = Boolean(firebaseConfig.apiKey && firebaseConfig.projectId);

const app = getApps().length > 0 ? getApp() : initializeApp(firebaseConfig);
export const auth = getAuth(app);
export const googleProvider = new GoogleAuthProvider();
export {
  signInWithPopup,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendEmailVerification,
  applyActionCode,
  signOut,
  onAuthStateChanged,
  updateProfile,
};

// 2. Automate Client-Side Identity Injection & WebSocket JWT binding
if (typeof window !== 'undefined') {
  onAuthStateChanged(auth, async (user) => {
    if (user) {
      try {
        const token = await user.getIdToken();
        rethink.setIdentity({ token });
        console.log('[RethinkClient] Auth credentials synchronized successfully.');
      } catch (err) {
        console.error('[RethinkClient] Failed to fetch ID Token:', err);
      }
    } else {
      rethink.setIdentity(null);
      console.log('[RethinkClient] Auth identity cleared.');
    }
  });
}
